import React from 'react';
import { Info, Calendar, Briefcase, Flag, Clock, Calculator } from 'lucide-react';

/**
 * Компонент с информацией о календарях и датах
 */
const InfoContent = () => {
  return (
    <article className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mt-8">
      <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
        <Info className="w-6 h-6 text-cyan-500" />
        Всё о календарях и расчетах дат в России
      </h2>
      
      <div className="prose prose-cyan dark:prose-invert max-w-none prose-sm sm:prose-base">
        <h3 className="flex items-center gap-2">
          <Calendar className="w-5 h-5 text-cyan-500" />
          Календарная система в России
        </h3>
        <p>
          <strong>Григорианский календарь</strong> используется в России с 14 февраля 1918 года. До этого использовался юлианский календарь, который на момент перехода отставал на 13 дней. Россия была одной из последних стран, совершивших переход на григорианский календарь.
        </p>
        <p>
          В современной России официальное летоисчисление ведется по григорианскому календарю, который является международным стандартом. Григорианский календарь учитывает високосные годы и имеет строгую систему коррекции, что делает его достаточно точным для гражданских целей.
        </p>

        <h3 className="flex items-center gap-2">
          <Briefcase className="w-5 h-5 text-cyan-500" />
          Рабочий календарь и трудовое законодательство
        </h3>
        <p>
          <strong>Производственный календарь</strong> – это важный инструмент для планирования рабочего времени в России. Он составляется с учетом Трудового кодекса РФ и ежегодных постановлений Правительства о переносе выходных дней.
        </p>
        <p>
          Согласно статье 112 Трудового кодекса РФ, в России установлены следующие нерабочие праздничные дни:
        </p>
        <ul>
          <li>1, 2, 3, 4, 5, 6, 8 января – Новогодние каникулы;</li>
          <li>7 января – Рождество Христово;</li>
          <li>23 февраля – День защитника Отечества;</li>
          <li>8 марта – Международный женский день;</li>
          <li>1 мая – Праздник Весны и Труда;</li>
          <li>9 мая – День Победы;</li>
          <li>12 июня – День России;</li>
          <li>4 ноября – День народного единства.</li>
        </ul>
        <p>
          Если праздничный день выпадает на выходной, то выходной день переносится на следующий после праздничного рабочий день. Правительство РФ может переносить выходные дни на другие дни в целях рационального использования работниками выходных и нерабочих праздничных дней.
        </p>

        <h3 className="flex items-center gap-2">
          <Flag className="w-5 h-5 text-cyan-500" />
          Региональные особенности календаря
        </h3>
        <p>
          В России, помимо федеральных праздников, существуют и региональные праздничные дни, которые устанавливаются местными законодательными органами. В некоторых регионах эти дни являются выходными.
        </p>
        <p>
          <strong>Примеры региональных праздников:</strong>
        </p>
        <ul>
          <li>Республика Татарстан: Ураза-байрам, Курбан-байрам, День Республики Татарстан (30 августа);</li>
          <li>Республика Башкортостан: День Республики Башкортостан (11 октября);</li>
          <li>Республика Саха (Якутия): День Республики Саха (27 апреля), Ысыах (национальный праздник летнего солнцестояния);</li>
          <li>Республика Крым: День воссоединения Крыма с Россией (18 марта);</li>
          <li>Санкт-Петербург: День основания Санкт-Петербурга (27 мая) – не является выходным, но широко отмечается.</li>
        </ul>

        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800 my-6">
          <h4 className="text-blue-700 dark:text-blue-300 font-medium">Важно знать для планирования</h4>
          <p className="text-blue-600 dark:text-blue-400 text-sm mb-0">
            При планировании деловых встреч, командировок и других рабочих мероприятий всегда сверяйтесь с актуальным производственным календарем. Особенно это касается периодов новогодних праздников и майских выходных, когда может быть несколько последовательных нерабочих дней.
          </p>
        </div>

        <h3 className="flex items-center gap-2">
          <Clock className="w-5 h-5 text-cyan-500" />
          Часовые пояса России
        </h3>
        <p>
          Россия – крупнейшая страна мира, которая протянулась через 11 часовых поясов. С 26 октября 2014 года в России введено постоянное время без сезонных переходов на летнее/зимнее время.
        </p>
        <p>
          Официально в России установлены следующие часовые зоны относительно UTC (всемирного координированного времени):
        </p>
        <ul>
          <li>MSK-1 (UTC+2): Калининградская область;</li>
          <li>MSK (UTC+3): Москва и большинство европейских регионов России;</li>
          <li>MSK+1 (UTC+4): Самарская область, Удмуртия и др.;</li>
          <li>MSK+2 (UTC+5): Екатеринбург, Челябинск, Пермь и др.;</li>
          <li>MSK+3 (UTC+6): Омск, Томск и др.;</li>
          <li>MSK+4 (UTC+7): Новосибирск, Красноярск и др.;</li>
          <li>MSK+5 (UTC+8): Иркутск, Улан-Удэ и др.;</li>
          <li>MSK+6 (UTC+9): Якутск, Чита и др.;</li>
          <li>MSK+7 (UTC+10): Хабаровск, Владивосток и др.;</li>
          <li>MSK+8 (UTC+11): Магадан, Сахалин и др.;</li>
          <li>MSK+9 (UTC+12): Камчатка, Чукотка.</li>
        </ul>

        <h3 className="flex items-center gap-2">
          <Calculator className="w-5 h-5 text-cyan-500" />
          Правильный расчет дат в юридических и бизнес-контекстах
        </h3>
        <p>
          При расчете сроков для юридических документов важно учитывать следующие правила, установленные законодательством РФ:
        </p>
        <ul>
          <li><strong>Гражданский кодекс РФ</strong>: Согласно статье 190-194 ГК РФ, срок определяется календарной датой или истечением периода времени. Течение срока начинается на следующий день после даты или наступления события. Если последний день срока приходится на нерабочий день, днем окончания срока считается ближайший следующий за ним рабочий день.</li>
          <li><strong>Процессуальные сроки</strong>: В соответствии с ГПК РФ, АПК РФ и КАС РФ, процессуальные сроки исчисляются годами, месяцами и днями. В сроки, исчисляемые днями, не включаются нерабочие дни.</li>
          <li><strong>Трудовое законодательство</strong>: При расчете отпусков, сроков предупреждения и выплат учитываются календарные дни. Однако выплаты и уведомления, выпадающие на выходные и праздники, переносятся на предшествующий рабочий день.</li>
          <li><strong>Налоговый кодекс РФ</strong>: Согласно статье 6.1 НК РФ, если последний день срока приходится на выходной или нерабочий праздничный день, то днем окончания срока считается ближайший следующий за ним рабочий день.</li>
        </ul>

        <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-100 dark:border-yellow-800 my-6">
          <h4 className="text-yellow-700 dark:text-yellow-300 font-medium flex items-center gap-2">
            Советы по работе с датами в России
          </h4>
          <ul className="text-yellow-600 dark:text-yellow-400 text-sm mb-0">
            <li>Всегда учитывайте переносы выходных дней при долгосрочном планировании</li>
            <li>При международном сотрудничестве обращайте внимание на разницу в праздничных днях</li>
            <li>Для юридических документов указывайте даты в формате "ДД" месяц ГГГГ года (например, "01" января 2023 года)</li>
            <li>В деловой документации рекомендуется использовать формат ДД.ММ.ГГГГ (например, 01.01.2023)</li>
            <li>При указании сроков всегда уточняйте, включаются ли в расчет выходные и праздничные дни</li>
          </ul>
        </div>

        <h3>Полезные источники информации о календарях и датах</h3>
        <ul>
          <li>Официальный производственный календарь на портале <a href="http://publication.pravo.gov.ru/" target="_blank" rel="noopener noreferrer">Официального опубликования правовых актов</a></li>
          <li>Постановления Правительства РФ о переносе выходных дней</li>
          <li>Трудовой кодекс РФ, статьи 111-113 (выходные и праздничные дни)</li>
          <li>Региональные законы о праздничных днях субъектов РФ</li>
          <li>Федеральная служба по труду и занятости (Роструд)</li>
        </ul>

        <p className="font-semibold mt-6">
          Наш калькулятор дат поможет вам точно рассчитать даты с учетом особенностей российского календаря, региональных праздников и трудового законодательства!
        </p>
      </div>
    </article>
  );
};

export default InfoContent;